import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit3, Trash2, CalendarPlus, Plus, Loader2, X } from 'lucide-react';
import { TableLoader } from '../Common/Loader';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  toggleSelectScheme,
  toggleSelectAllSchemes,
  setPage,
  fetchSchemes,
  deleteScheme,
} from '../../store/schemeSlice';
import { extendScheme, batchExtendSchemeValidity } from '../../api/scheme';

export const SchemeTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { schemes, selectedSchemeIds, loading, totalEntries, filters } = useAppSelector(
    (state) => state.scheme
  );

  // Extend Modal State
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [targetExtendScheme, setTargetExtendScheme] = useState<{ id: string; name: string } | null>(null);
  const [extendedDays, setExtendedDays] = useState<number>(15);
  const [remarks, setRemarks] = useState<string>('Extended validity');
  const [submittingExtend, setSubmittingExtend] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);

  const isAllSelected = schemes.length > 0 && selectedSchemeIds.length === schemes.length;

  const handleSelectAll = () => {
    dispatch(toggleSelectAllSchemes());
  };

  const handleSelectRow = (id: string) => {
    dispatch(toggleSelectScheme(id));
  };

  const handleView = (id: string) => {
    navigate(`/scheme/details/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/scheme/edit/${id}`);
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete scheme "${name}"?`)) {
      try {
        await dispatch(deleteScheme(id)).unwrap();
        dispatch(fetchSchemes());
      } catch (err: any) {
        alert(err || 'Failed to delete scheme.');
      }
    }
  };

  // Open Extend Modal for single scheme
  const handleOpenSingleExtend = (id: string, name: string) => {
    setTargetExtendScheme({ id, name });
    setExtendedDays(15);
    setRemarks('Extended validity');
    setExtendError(null);
    setIsExtendModalOpen(true);
  };

  // Open Extend Modal for bulk selected schemes
  const handleOpenBulkExtend = () => {
    if (selectedSchemeIds.length === 0) {
      alert('Please select at least one scheme to extend.');
      return;
    }
    setTargetExtendScheme(null);
    setExtendedDays(15);
    setRemarks('Extended validity for selected schemes');
    setExtendError(null);
    setIsExtendModalOpen(true);
  };

  const handleSubmitExtend = async (e: React.FormEvent) => {
    e.preventDefault();
    setExtendError(null);

    if (extendedDays <= 0) {
      setExtendError('Please enter a valid positive number of extension days.');
      return;
    }

    try {
      setSubmittingExtend(true);
      if (targetExtendScheme) {
        // Single scheme extend API call: POST /erp/schemes/:id/extend
        await extendScheme(targetExtendScheme.id, extendedDays, remarks);
      } else if (selectedSchemeIds.length > 0) {
        // Bulk scheme extend API call
        await batchExtendSchemeValidity(selectedSchemeIds, (extendedDays === 30 ? 30 : 15) as any, remarks);
      }

      setIsExtendModalOpen(false);
      dispatch(fetchSchemes());
    } catch (err: any) {
      setExtendError(err?.message || 'Failed to extend scheme validity. Please try again.');
    } finally {
      setSubmittingExtend(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
    dispatch(fetchSchemes());
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
      {/* Table Section Top Bar */}
      <div className="flex flex-wrap items-center justify-between px-5 py-4 gap-3 border-b border-gray-100 bg-slate-50/50">
        <div className="flex items-center gap-3">
          <h2 className="text-base md:text-lg font-bold text-gray-800 tracking-tight m-0">
            Schemes ({totalEntries})
          </h2>
          {selectedSchemeIds.length > 0 && (
            <span className="px-2.5 py-0.5 bg-red-100 text-[#B71C1C] text-xs font-bold rounded-full">
              {selectedSchemeIds.length} Selected
            </span>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2.5">
          {selectedSchemeIds.length > 0 && (
            <button
              onClick={handleOpenBulkExtend}
              className="px-3.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300/80 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <CalendarPlus className="w-4 h-4 text-amber-700" />
              <span>Extend Selected ({selectedSchemeIds.length})</span>
            </button>
          )}

          <button
            onClick={() => navigate('/scheme/create')}
            className="go_erp-bg hover:opacity-95 text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer shadow-xs transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Create Scheme</span>
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto select-none">
        <table className="w-full text-left border-collapse min-w-[850px]">
          <thead>
            <tr className="bg-[#FFF9E6] text-gray-600 text-xs font-semibold">
              <th className="py-3.5 px-4 w-10 text-center">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-[#B71C1C] focus:ring-[#B71C1C] cursor-pointer"
                />
              </th>
              <th className="py-3.5 px-4">Scheme Name</th>
              {/* <th className="py-3.5 px-4">Location (Zone/City/Territory)</th> */}
              <th className="py-3.5 px-4">Dist. Type</th>
              <th className="py-3.5 px-4">Product</th>
              <th className="py-3.5 px-4 text-center">Eligible/ Products</th>
              <th className="py-3.5 px-4 text-center">Free Products</th>
              <th className="py-3.5 px-4">Validity</th>
              <th className="py-3.5 px-4 text-center">Status</th>
              <th className="py-3.5 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-xs md:text-sm">
            {loading ? (
              <TableLoader colSpan={10} text="Loading schemes..." />
            ) : schemes.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-10 text-center text-gray-500">
                  No schemes found matching criteria.
                </td>
              </tr>
            ) : (
              schemes.map((scheme) => {
                const isSelected = selectedSchemeIds.includes(scheme.id);
                return (
                  <tr
                    key={scheme.id}
                    className={`hover:bg-gray-50/80 transition-colors ${
                      isSelected ? 'bg-red-50/20' : ''
                    }`}
                  >
                    <td className="py-3.5 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectRow(scheme.id)}
                        className="rounded border-gray-300 text-[#B71C1C] focus:ring-[#B71C1C] cursor-pointer"
                      />
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-gray-900">
                        {scheme.name}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] font-mono tracking-tight mt-1">
                        <span className="bg-slate-100 text-slate-700 font-semibold px-1.5 py-0.5 rounded">
                          ID: {scheme.code || `${scheme.id}`}
                        </span>
                        {/* {scheme.sapCode && scheme.sapCode !== '-' && (
                          <span className="bg-amber-50 text-amber-800 border border-amber-200/60 font-semibold px-1.5 py-0.5 rounded">
                            SAP Code: {scheme.sapCode}
                          </span>
                        )} */}
                      </div>
                    </td>
                    {/* <td className="py-3.5 px-4 text-gray-800 font-medium">{scheme.zoneCity}</td> */}
                    <td className="py-3.5 px-4 text-gray-800 font-semibold">
                      {typeof scheme.distType === 'object' && scheme.distType ? (scheme.distType as any).name : String(scheme.distType || '-')}
                    </td>
                    <td className="py-3.5 px-4 text-gray-700 font-medium">{scheme.product}</td>
                    <td className="py-3.5 px-4 text-center text-gray-600 font-medium">
                      {scheme.eligibleProducts} {scheme.eligibleUnitType || 'Units'}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span className="text-[#16A34A] font-bold text-xs px-2 py-0.5 rounded">
                        +{scheme.freeProducts} Free
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-600 whitespace-nowrap">{scheme.validity}</td>
                    <td className="py-3.5 px-4 text-center text-xs">
                      <span
                        className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full inline-block ${
                          scheme.status === 'Active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : scheme.status === 'Upcoming'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-rose-100 text-rose-700'
                        }`}
                      >
                        {scheme.status}
                      </span>
                    </td>

                    {/* Actions Column: View, Edit, Extend, Delete */}
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleView(scheme.id)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(scheme.id)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="Edit Scheme"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenSingleExtend(scheme.id, scheme.name)}
                          className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="Extend Validity"
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(scheme.id, scheme.name)}
                          className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="Delete Scheme"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-wrap items-center justify-between px-5 py-4 border-t border-gray-100 text-xs text-gray-500 gap-3">
        <div>
          Showing 1 to {schemes.length} of {totalEntries} entries
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => handlePageChange(Math.max(1, filters.page - 1))}
            disabled={filters.page === 1}
            className="px-3 py-1 bg-[#FFF9E6] hover:bg-[#FFF2C6] text-amber-900 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            Previous
          </button>
          <button
            className="w-7 h-7 bg-[#B71C1C] text-white font-bold rounded-full flex items-center justify-center cursor-default shadow-2xs"
          >
            {filters.page}
          </button>
          <button
            onClick={() => handlePageChange(filters.page + 1)}
            className="px-3 py-1 bg-[#FFF9E6] hover:bg-[#FFF2C6] text-amber-900 rounded-full font-medium transition-colors cursor-pointer"
          >
            Next
          </button>
        </div>
      </div>

      {/* Extend Scheme Modal */}
      {isExtendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center">
                  <CalendarPlus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-800 tracking-tight m-0">
                    Extend Scheme Validity
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    {targetExtendScheme ? targetExtendScheme.name : `${selectedSchemeIds.length} Schemes Selected`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsExtendModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {extendError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl">
                {extendError}
              </div>
            )}

            <form onSubmit={handleSubmitExtend} className="space-y-4">
              {/* Quick Days Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Extension Days*
                </label>
                <div className="flex items-center gap-2 mb-2">
                  {[15, 30, 45, 60].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExtendedDays(d)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                        extendedDays === d
                          ? 'go_erp-bg text-white border-[#C1272D]'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      +{d} Days
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  required
                  min={1}
                  value={extendedDays}
                  onChange={(e) => setExtendedDays(Number(e.target.value))}
                  placeholder="Enter custom number of days"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
                />
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Extension Remarks / Reason
                </label>
                <textarea
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g. Extended for summer demand"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
                />
              </div>

              {/* Modal Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsExtendModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingExtend}
                  className="px-5 py-2 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {submittingExtend && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{submittingExtend ? 'Extending...' : 'Confirm Extension'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemeTable;
